import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Copy,
  Share2,
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle2,
  Loader2,
  Banknote,
} from "lucide-react";
import { toast } from "sonner";

export default function AffiliatePage() {
  const [data, setData] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [pixKey, setPixKey] = useState("");
  const [savingPix, setSavingPix] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openPayout, setOpenPayout] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [me, list, pixRes] = await Promise.all([
      api.get("/affiliate/me"),
      api.get("/affiliate/commissions"),
      api.get("/affiliate/pix-key"),
    ]);
    setData(me.data);
    setCommissions(list.data.commissions);
    if (pixRes.data.pix_key) setPixKey(pixRes.data.pix_key);
  };

  const link = data
    ? `${window.location.origin}/login?ref=${data.referral_code}`
    : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Link copiado!");
  };

  const share = async () => {
    if (navigator.share) {
      await navigator.share({
        title: "AutoPost BR — Automação de posts",
        text: "Use meu link para se cadastrar e começar a automatizar suas publicações:",
        url: link,
      });
    } else {
      await copyLink();
    }
  };

  const savePixKey = async () => {
    if (!pixKey.trim()) return toast.error("Informe sua chave PIX");
    setSavingPix(true);
    try {
      await api.post("/affiliate/pix-key", { pix_key: pixKey });
      toast.success("Chave PIX salva!");
    } catch {
      toast.error("Erro ao salvar chave PIX");
    } finally {
      setSavingPix(false);
    }
  };

  const requestPayout = async () => {
    if (!pixKey.trim()) return toast.error("Informe sua chave PIX");
    setBusy(true);
    try {
      const { data: r } = await api.post("/affiliate/payout", { pix_key: pixKey });
      toast.success(`Solicitação de R$ ${r.amount.toFixed(2)} enviada! Processaremos em até 7 dias úteis.`);
      setOpenPayout(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao solicitar saque"));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="p-12 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

  return (
    <div className="p-6 md:p-8 max-w-[1300px]">
      <div className="mb-8">
        <p className="font-mono-label text-zinc-500 mb-1">Programa de Afiliados</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Indique e ganhe 20% recorrente
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Para cada usuário que assinar Pro ou Agência usando seu link, você ganha {(data.rate * 100).toFixed(0)}% de comissão
          em todas as cobranças, todo mês, enquanto a assinatura estiver ativa.
        </p>
      </div>

      {/* Hero card with link */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-sm p-7 mb-8 text-white">
        <p className="font-mono-label text-blue-200 mb-3">Seu link de indicação</p>
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1 bg-white/10 backdrop-blur border border-white/20 rounded-sm px-4 py-3 font-mono text-sm break-all">
            {link}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={copyLink}
              data-testid="affiliate-copy-link"
              className="rounded-sm bg-white text-blue-700 hover:bg-blue-50 h-12 px-5"
            >
              <Copy className="h-4 w-4 mr-2" /> Copiar
            </Button>
            <Button
              onClick={share}
              data-testid="affiliate-share"
              variant="outline"
              className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10 h-12 px-5"
            >
              <Share2 className="h-4 w-4 mr-2" /> Compartilhar
            </Button>
          </div>
        </div>
        <p className="text-xs text-blue-200 mt-4">
          Código:{" "}
          <span className="font-mono font-bold tracking-widest text-white">
            {data.referral_code}
          </span>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Users}
          label="Indicações"
          value={data.stats.referrals}
          sub={`${data.stats.paying_referrals} pagantes`}
          color="blue"
          testid="stat-referrals"
        />
        <StatCard
          icon={TrendingUp}
          label="Total ganho"
          value={fmt(data.stats.earnings_total)}
          sub="ao longo de todo o tempo"
          color="green"
          testid="stat-total"
        />
        <StatCard
          icon={Clock}
          label="A receber"
          value={fmt(data.stats.earnings_pending)}
          sub="aprovado, aguardando saque"
          color="amber"
          testid="stat-pending"
        />
        <StatCard
          icon={CheckCircle2}
          label="Já pago"
          value={fmt(data.stats.earnings_paid)}
          sub="depositado na sua conta"
          color="zinc"
          testid="stat-paid"
        />
      </div>

      {/* Chave PIX salva */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-4">
        <h3 className="font-display text-base font-semibold mb-1 flex items-center gap-2">
          <Banknote className="h-4 w-4 text-blue-600" /> Sua chave PIX para recebimento
        </h3>
        <p className="text-xs text-zinc-500 mb-3">Salve sua chave PIX uma vez e ela será usada em todos os saques futuros.</p>
        <div className="flex gap-2">
          <Input
            value={pixKey}
            onChange={(e) => setPixKey(e.target.value)}
            placeholder="CPF, e-mail, telefone ou chave aleatória"
            className="rounded-sm h-10 flex-1"
          />
          <Button
            onClick={savePixKey}
            disabled={savingPix}
            variant="outline"
            className="rounded-sm h-10 px-4"
          >
            {savingPix && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>

      {/* Payout button */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-8 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <Banknote className="h-5 w-5 text-green-600" /> Solicitar saque via PIX
          </h3>
          <p className="text-sm text-zinc-500 mt-1">
            Saque mínimo R$ 50,00. Processamos em até 7 dias úteis.
          </p>
        </div>
        <Dialog open={openPayout} onOpenChange={setOpenPayout}>
          <DialogTrigger asChild>
            <Button
              disabled={data.stats.earnings_pending < 50}
              data-testid="affiliate-request-payout"
              className="rounded-sm bg-green-600 hover:bg-green-700 h-11"
            >
              Solicitar saque
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display">Confirmar saque PIX</DialogTitle>
              <DialogDescription>
                Você sacará todo o valor pendente: {fmt(data.stats.earnings_pending)}.
              </DialogDescription>
            </DialogHeader>
            <div className="py-3 space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-sm">
                <span className="text-zinc-500 font-mono-label">Chave PIX: </span>
                <span className="font-semibold">{pixKey || "—"}</span>
              </div>
              {!pixKey && (
                <div>
                  <label className="font-mono-label text-zinc-500 mb-2 block">Chave PIX</label>
                  <Input
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder="CPF, e-mail, telefone ou aleatória"
                    className="rounded-sm h-11"
                  />
                </div>
              )}
              <p className="text-xs text-zinc-400">Após confirmar, nossa equipe processará o PIX em até 7 dias úteis.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenPayout(false)} className="rounded-sm">
                Cancelar
              </Button>
              <Button
                onClick={requestPayout}
                disabled={busy}
                className="rounded-sm bg-green-600 hover:bg-green-700"
                data-testid="payout-submit"
              >
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar solicitação
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Commissions table */}
      <h2 className="font-display text-xl font-semibold mb-4">Histórico de comissões</h2>
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[1fr_140px_140px_140px_140px] gap-4 px-5 py-3 border-b border-zinc-100 font-mono-label text-zinc-500 bg-zinc-50">
          <span>Indicado</span>
          <span>Venda</span>
          <span>Comissão</span>
          <span>Status</span>
          <span>Data</span>
        </div>
        {commissions.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500">
            Nenhuma comissão ainda. Compartilhe seu link e comece a ganhar!
          </div>
        ) : (
          commissions.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-[1fr_140px_140px_140px_140px] gap-4 px-5 py-4 items-center border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
            >
              <div className="text-sm truncate font-medium">{c.referred_user_email}</div>
              <div className="font-mono text-sm">{fmt(c.sale_amount)}</div>
              <div className="font-mono text-sm font-semibold text-green-600">
                {fmt(c.commission_amount)}
              </div>
              <div>
                <CommissionBadge status={c.status} />
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                {new Date(c.created_at).toLocaleDateString("pt-BR")}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, color, testid }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    zinc: "bg-zinc-100 text-zinc-700",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${colors[color]}`}>
          <Icon className="h-4 w-4" strokeWidth={2} />
        </div>
        <span className="font-mono-label text-zinc-500">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
};

function CommissionBadge({ status }) {
  const map = {
    approved: { label: "Aprovado", cls: "border-blue-200 text-blue-700 bg-blue-50" },
    pending: { label: "Pendente", cls: "border-zinc-200 text-zinc-700 bg-zinc-50" },
    requested: { label: "Saque pedido", cls: "border-amber-200 text-amber-700 bg-amber-50" },
    paid: { label: "Pago", cls: "border-green-200 text-green-700 bg-green-50" },
  };
  const s = map[status] || map.pending;
  return <Badge variant="outline" className={`rounded-sm font-mono-label ${s.cls}`}>{s.label}</Badge>;
}
